/**
 * Data access for `invoices`: invoices, templates and the workspace default ("Invoice
 * Settings") share the collection, told apart by `abstractType`. Totals, numbering and copies
 * are computed by server/pb_hooks/main.pb.js; API rules scope everything to the workspace.
 */
import { LABEL_NAMES } from "@/lib/invoice/labels";
import {
  invoiceFormSchema,
  toFormDate,
  VAT_MODES,
  type AbstractType,
  type InvoiceFormValues,
  type InvoiceStatus,
  type VatMode,
} from "@/lib/invoice/schema";
import type {
  CustomersResponse,
  InvoicesResponse,
  PaymentMethodsResponse,
} from "@/lib/core/pb-types";
import type { RecipientCustomer } from "@/lib/customer/recipient";
import type { PaymentDetails } from "./payment-methods";
import { pb } from "@/lib/core/pocketbase";
import { INVOICE_TEMPLATES } from "@/lib/invoice/templates";
import { sortBy } from "./sort";
import { currentWorkspaceId } from "./workspaces";
import type { Locale } from "@/i18n/languages";
import type { LabelName } from "@/lib/invoice/labels";
import { toCreditNoteLabels, toInvoiceLabels, toQuoteLabels } from "@/lib/invoice/quote-labels";
import { customerInvoiceChanges } from "@/lib/customer/invoice-defaults";
import { EMPTY_FILTERS, filterExpression, type ListFilters } from "@/lib/invoice/list-filters";
import { toCustomerInput } from "./customers";

/** Rows per page unless the user picked another size (see PAGE_SIZES). */
export const PAGE_SIZE = 25;
export const PAGE_SIZES = [10, 25, 50, 100] as const;

type RawItem = {
  title?: unknown;
  description?: unknown;
  quantity?: unknown;
  unit?: unknown;
  unitPrice?: unknown;
  taxRate?: unknown;
  exempt?: unknown;
  exemptionReason?: unknown;
};
export type InvoiceRecord = InvoicesResponse<
  /** A finalised invoice's snapshot of its customer (lib/invoice/document-pdf.ts), else null. */
  RecipientCustomer,
  RawItem[],
  /** A finalised invoice's snapshot of its payment details, else null. */
  PaymentDetails,
  /** A finalised invoice's snapshot of its sender (lib/workspace/company.ts snapshotOf), else null. */
  unknown,
  { customer?: CustomersResponse; sourceQuote?: InvoicesResponse; creditFor?: InvoicesResponse }
>;
export type DocumentKind = Exclude<AbstractType, "default">;

/** URL sort keys -> PocketBase sort expressions (anything else sorts by newest). */
const SORT_FIELDS: Record<string, string> = {
  invoiceNumber: "invoiceNumber",
  invoiceTitle: "invoiceTitle",
  customer: "customer.name",
  issueDate: "issueDate",
  dueDate: "dueDate",
  dueDateInDays: "dueDateInDays",
  status: "status",
  total: "total",
};

export type ListParams = {
  kind: DocumentKind;
  page?: number;
  perPage?: number;
  sort?: string;
  direction?: "asc" | "desc";
  filters?: ListFilters;
  /** Templates only: just invoice or quote templates. */
  templateKind?: TemplateKind;
};

export function sortExpression(sort?: string, direction?: "asc" | "desc"): string {
  return sortBy(SORT_FIELDS, sort, direction);
}

/** The workspace's documents of one kind, narrowed by the list filters. */
function listFilter(kind: DocumentKind, filters?: ListFilters, templateKind?: TemplateKind) {
  const { expression, params } = filterExpression(filters ?? EMPTY_FILTERS);
  const parts = ["workspace = {:workspace} && abstractType = {:kind}"];
  if (kind === "template" && templateKind) parts.push(templateKindFilter(templateKind));
  if (expression) parts.push(expression);
  return pb.filter(parts.join(" && "), { ...params, workspace: currentWorkspaceId(), kind });
}

export async function listInvoices({
  kind,
  page = 1,
  perPage = PAGE_SIZE,
  sort,
  direction,
  filters,
  templateKind,
}: ListParams) {
  return pb.collection("invoices").getList<InvoiceRecord>(Math.max(1, page), perPage, {
    filter: listFilter(kind, filters, templateKind),
    sort: sortExpression(sort, direction),
    expand: "customer",
  });
}

export type ListTotalsRow = Pick<InvoiceRecord, "total" | "currency" | "status">;

/** Amount and status of every document the filters match (the totals under the list). */
export async function listTotals(kind: DocumentKind, filters?: ListFilters) {
  return pb.collection("invoices").getFullList<ListTotalsRow>({
    filter: listFilter(kind, filters),
    fields: "total,currency,status",
  });
}

/** A document with what its PDF and CSV row need: customer, payment method, credited invoice. */
export type ExportRecord = InvoicesResponse<
  RecipientCustomer,
  RawItem[],
  PaymentDetails,
  unknown,
  {
    customer?: CustomersResponse;
    creditFor?: InvoicesResponse;
    paymentMethod?: PaymentMethodsResponse;
  }
>;

/**
 * The documents to export, oldest first: the selected ones (`ids`), or everything the list's
 * filters match (all pages).
 */
export async function listForExport(
  kind: DocumentKind,
  selection: { ids: string[] } | { filters: ListFilters },
): Promise<ExportRecord[]> {
  const filter =
    "ids" in selection
      ? pb.filter(
          `workspace = {:workspace} && (${selection.ids.map((_, i) => `id = {:id${i}}`).join(" || ") || "id = ''"})`,
          {
            workspace: currentWorkspaceId(),
            ...Object.fromEntries(selection.ids.map((id, i) => [`id${i}`, id])),
          },
        )
      : listFilter(kind, selection.filters);
  return pb.collection("invoices").getFullList<ExportRecord>({
    filter,
    sort: "issueDate,invoiceNumber",
    expand: "customer,creditFor,paymentMethod",
  });
}

/** Every invoice of the workspace, newest change first (dashboard). */
export async function listAllInvoices(): Promise<InvoiceRecord[]> {
  return pb.collection("invoices").getFullList<InvoiceRecord>({
    filter: pb.filter('workspace = {:workspace} && abstractType = "invoice"', {
      workspace: currentWorkspaceId(),
    }),
    sort: "-updated",
    expand: "customer",
  });
}

export type CustomerDocument = Pick<
  InvoiceRecord,
  | "id"
  | "abstractType"
  | "invoiceNumber"
  | "status"
  | "total"
  | "currency"
  | "currencyFormat"
  | "issueDate"
  | "dueDate"
>;

/** A customer's invoices and quotes, newest first (the customer page's sidebar). */
export async function listCustomerDocuments(customerId: string): Promise<CustomerDocument[]> {
  return pb.collection("invoices").getFullList<CustomerDocument>({
    filter: pb.filter(
      'workspace = {:workspace} && customer = {:customer} && (abstractType = "invoice" || abstractType = "quote")',
      { workspace: currentWorkspaceId(), customer: customerId },
    ),
    sort: "-issueDate,-created",
    fields: "id,abstractType,invoiceNumber,status,total,currency,currencyFormat,issueDate,dueDate",
  });
}

/** Quotes sent and waiting for an answer (the dashboard's "Open quotes"). */
export async function listOpenQuotes(): Promise<
  Pick<InvoiceRecord, "id" | "total" | "currency">[]
> {
  return pb.collection("invoices").getFullList({
    filter: pb.filter('workspace = {:workspace} && abstractType = "quote" && status = "sent"', {
      workspace: currentWorkspaceId(),
    }),
    fields: "id,total,currency",
  });
}

export async function getInvoice(id: string): Promise<InvoiceRecord> {
  return pb
    .collection("invoices")
    .getOne<InvoiceRecord>(id, { expand: "customer,sourceQuote,creditFor" });
}

/** The workspace's "Invoice Settings" record that new invoices and templates copy. */
export async function getDefaultInvoice(): Promise<InvoiceRecord> {
  const workspace = await pb.collection("workspaces").getOne(currentWorkspaceId());
  return getInvoice(workspace.defaultInvoice);
}

const toNumber = (value: unknown, fallback: number) => {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(n) ? n : fallback;
};

/** A line's own VAT rate (0-100), or null when it takes the invoice's standard rate. */
const rateOrNull = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const n = toNumber(value, NaN);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
};

/** Record -> editor values (tolerates empty/legacy fields). */
export function toFormValues(record: InvoiceRecord): InvoiceFormValues {
  const labels = Object.fromEntries(LABEL_NAMES.map((name) => [name, record[name] ?? ""]));
  const items = Array.isArray(record.items) ? record.items : [];
  return invoiceFormSchema.parse({
    ...labels,
    invoiceTitle: record.invoiceTitle,
    invoiceNumber: record.invoiceNumber,
    dueDateInDays: record.dueDateInDays || 30,
    quoteValidityDays: record.quoteValidityDays || 30,
    emailSubject: record.emailSubject ?? "",
    emailMessage: record.emailMessage ?? "",
    quoteEmailSubject: record.quoteEmailSubject ?? "",
    quoteEmailMessage: record.quoteEmailMessage ?? "",
    issueDate: toFormDate(record.issueDate),
    dueDate: toFormDate(record.dueDate),
    fromDate: toFormDate(record.fromDate),
    toDate: toFormDate(record.toDate),
    customer: record.customer ?? "",
    paymentMethod: record.paymentMethod ?? "",
    status: record.status || "draft",
    currency: record.currency || "EUR",
    currencyFormat: record.currencyFormat || "en-US",
    dateFormat: record.dateFormat || "YYYY-MM-DD",
    template: INVOICE_TEMPLATES.some((tpl) => tpl.id === record.template)
      ? record.template
      : "montana",
    accentColor: /^#[0-9a-f]{6}$/.test(record.accentColor ?? "") ? record.accentColor! : "",
    footNoteLeft: record.footNoteLeft,
    footNoteRight: record.footNoteRight,
    discount: Math.min(100, Math.max(0, toNumber(record.discount, 0))),
    tax: Math.min(100, Math.max(0, toNumber(record.tax, 0))),
    vatMode: VAT_MODES.includes(record.vatMode as VatMode) ? record.vatMode : "standard",
    vatNote: record.vatNote ?? "",
    hidePaymentQr: record.hidePaymentQr ?? false,
    items: items.map((item) => ({
      title: String(item?.title ?? ""),
      description: String(item?.description ?? ""),
      quantity: Math.max(0, toNumber(item?.quantity, 1)),
      unit: String(item?.unit ?? "").slice(0, 20),
      unitPrice: toNumber(item?.unitPrice, 0),
      taxRate: rateOrNull(item?.taxRate),
      exempt: item?.exempt === true,
      exemptionReason: String(item?.exemptionReason ?? "").slice(0, 300),
    })),
  });
}

export async function saveInvoice(id: string, values: unknown): Promise<InvoiceRecord> {
  return pb.collection("invoices").update<InvoiceRecord>(id, invoiceFormSchema.parse(values));
}

/** Sets the status of one or several invoices (list row menu and bulk edit). */
export async function setInvoiceStatus(ids: string[], status: InvoiceStatus): Promise<void> {
  await Promise.all(ids.map((id) => pb.collection("invoices").update(id, { status })));
}

/** Replaces a draft's lines (the sample lines of a workspace's first invoice). */
export async function setInvoiceItems(id: string, items: unknown[]): Promise<InvoiceRecord> {
  return pb.collection("invoices").update<InvoiceRecord>(id, { items });
}

export async function deleteInvoice(id: string): Promise<void> {
  await pb.collection("invoices").delete(id);
}

/** What a template makes: invoices or quotes (older templates without a kind made invoices). */
export type TemplateKind = "invoice" | "quote";

export const templateKindOf = (record: Pick<InvoiceRecord, "templateKind">): TemplateKind =>
  record.templateKind === "quote" ? "quote" : "invoice";

/**
 * Server-side copy incl. files, numbering and dates (POST /api/blanqo/invoices/:id/duplicate).
 * `labels` replaces printed labels on the copy (quote <-> invoice wording); `templateKind` is
 * the kind of a new template made from the defaults. The server refuses copies against the
 * order of things (an invoice into a quote, a quote template into an invoice).
 */
export async function duplicateInvoice(
  sourceId: string,
  kind: DocumentKind,
  labels?: Partial<Record<LabelName, string>>,
  templateKind?: TemplateKind,
  creditNote = false,
) {
  return pb.send<InvoiceRecord>(`/api/blanqo/invoices/${encodeURIComponent(sourceId)}/duplicate`, {
    method: "POST",
    body: {
      abstractType: kind,
      ...(labels ? { labels } : {}),
      ...(templateKind ? { templateKind } : {}),
      ...(creditNote ? { creditNote } : {}),
    },
  });
}

/** Whether an invoice is a credit note (it credits another invoice). */
export const isCreditNote = (record: Pick<InvoiceRecord, "creditFor">) => Boolean(record.creditFor);

/**
 * "Create credit note": the server copies an issued invoice into a credit note with its own
 * number, negated prices and a link back (`creditFor`); the title and number label switch to
 * credit-note wording in the invoice's language.
 */
export async function createCreditNote(invoice: InvoiceRecord, language: Locale) {
  return duplicateInvoice(
    invoice.id,
    "invoice",
    toCreditNoteLabels(invoice, language),
    undefined,
    true,
  );
}

export type NewDocument = DocumentKind | { kind: "template"; templateKind: TemplateKind };

/**
 * "New invoice" / "New template" / "New quote": a copy of the workspace defaults. Quotes and
 * quote templates print "Quote" / "Valid until" / "Terms" in the language of the default labels
 * (else `language`).
 */
export async function createFromDefault(what: NewDocument, language: Locale = "en") {
  const { kind, templateKind } = typeof what === "string" ? { kind: what } : what;
  const quoteLike = kind === "quote" || templateKind === "quote";
  const defaults = await getDefaultInvoice();
  return duplicateInvoice(
    defaults.id,
    kind,
    quoteLike ? toQuoteLabels(defaults, language) : undefined,
    kind === "template" ? (templateKind ?? "invoice") : undefined,
  );
}

/** Templates of one kind, by name (the "From template" picker). */
export async function listTemplates(kind: TemplateKind): Promise<InvoiceRecord[]> {
  return pb.collection("invoices").getFullList<InvoiceRecord>({
    filter: pb.filter(
      `workspace = {:workspace} && abstractType = "template" && ${templateKindFilter(kind)}`,
      { workspace: currentWorkspaceId() },
    ),
    sort: "invoiceTitle",
    expand: "customer",
  });
}

const templateKindFilter = (kind: TemplateKind) =>
  kind === "quote" ? 'templateKind = "quote"' : 'templateKind != "quote"';

/**
 * "New invoice / quote for this customer": a new document with the customer picked and their
 * currency, payment terms and label language applied, as if the customer had been chosen in the
 * editor.
 */
export async function createForCustomer(
  kind: "invoice" | "quote",
  customer: CustomersResponse,
  { language = "en" }: { language?: Locale } = {},
) {
  const record = await createFromDefault(kind, language);
  const changes = customerInvoiceChanges(toCustomerInput(customer), toFormValues(record), kind);
  return pb
    .collection("invoices")
    .update<InvoiceRecord>(record.id, { customer: customer.id, ...changes });
}

/**
 * "Convert to invoice": the server copies the quote, numbers the invoice, links it to the quote
 * and marks the quote accepted; the labels go back to invoice wording.
 */
export async function convertQuote(quote: InvoiceRecord, language: Locale) {
  return duplicateInvoice(quote.id, "invoice", toInvoiceLabels(quote, language));
}
